/**
 * Chats layout — hosts the conversation history sidebar (New chat + Recents) next
 * to the chats home ("/chats") and conversation views ("/chats/$id"). The history
 * used to be a drill-in view inside DashboardShell; it now lives with the route it
 * belongs to, so the main sidebar stays a flat section nav.
 */

import { useState } from "react";
import { createFileRoute, Link, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { History, Plus } from "lucide-react";
import { cn } from "@/lib/cn";
import { TaskList } from "@/components/TaskList";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";

function ChatsLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [historyOpen, setHistoryOpen] = useState(false);

  const taskList = (close: boolean) => (
    <TaskList
      activeId={
        location.pathname.startsWith("/chats/")
          ? location.pathname.slice("/chats/".length).split("/")[0]
          : undefined
      }
      onSelect={(conv) => {
        if (close) setHistoryOpen(false);
        void navigate({
          to: "/chats/$conversationId",
          params: { conversationId: conv.conversation_id },
        });
      }}
    />
  );

  return (
    <div className="flex h-full flex-col lg:flex-row">
      <div className="flex h-11 shrink-0 items-center justify-between border-b border-border/60 px-2 lg:hidden">
        <Link
          to="/chats"
          className="flex h-8 items-center gap-1.5 rounded-md bg-primary px-2.5 text-control text-primary-foreground"
        >
          <Plus className="size-3.5" />
          New task
        </Link>
        <button
          type="button"
          onClick={() => setHistoryOpen(true)}
          className="flex h-8 items-center gap-1.5 rounded-md px-2.5 text-control text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <History className="size-3.5" />
          Recents
        </button>
      </div>
      {/* w-64 + 1px: the border-r eats into the box, so without the extra pixel
          the gutter-inset rows end up 1px narrower than the shell sidebar's. */}
      <aside className="hidden w-[calc(16rem+1px)] shrink-0 flex-col overflow-hidden border-r border-border/60 lg:flex">
        {/* pt-1.5 centers the h-8 button on the shell's logo row (and the
            studies top bar's Launch button): the panel sits 9px below the
            shell top, so 9 + 6 + 16 = the logo row's center at 13 + 18.
            px-1.5 matches that 6px inset horizontally — the shared gutter
            here, in the list below, and in the shell sidebar. */}
        <div className="px-1.5 pt-1.5">
          <Link
            to="/chats"
            className={cn(
              "flex h-8 items-center justify-center gap-2 rounded-md px-3 text-body transition-colors",
              "bg-black text-white hover:bg-black/90",
              location.pathname === "/chats" && "bg-black/90",
              "dark:border dark:border-border/60 dark:bg-background dark:text-foreground dark:hover:bg-muted",
              location.pathname === "/chats" && "dark:bg-muted"
            )}
          >
            <Plus className="h-4 w-4" />
            New task
          </Link>
        </div>

        {/* The thread history (TaskList brings its own "Recents" heading). */}
        <div className="min-h-0 flex-1 overflow-y-auto px-1.5">{taskList(false)}</div>
      </aside>

      <div className="min-w-0 flex-1 overflow-hidden">
        <Outlet />
      </div>

      <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
        <SheetContent side="left" className="w-80 max-w-[90vw] p-3" aria-describedby={undefined}>
          <SheetTitle>Recent tasks</SheetTitle>
          <div className="min-h-0 flex-1 overflow-y-auto">{taskList(true)}</div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

export const Route = createFileRoute("/_auth/_agents/chats")({
  component: ChatsLayout,
});
